import type { Teach, Study } from '@pocketprep/types'
import { groupModule } from '@/store/group/module'
import { organizationModule } from '@/store/organization/module'
import { instructorModule } from '@/store/instructor/module'
import { licenseModule } from '@/store/license/module'
import { examModule } from '@/store/exam/module'
import { studentModule } from '@/store/student/module'
import { isPromise } from '@/utils'
import type { TCompositeGroup } from '@/store/group/types'
import { computed } from 'vue'

const getCurrentOrgGroups = () => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const currentOrgGroups = currentOrganization
        && groupModule.state.currentOrgGroups[currentOrganization.objectId]?.value

    if (currentOrgGroups && !(isPromise(currentOrgGroups))) {
        return currentOrgGroups
    } else {
        return {}
    }
}

const currentOrgGroupsList = computed(() => {
    return Object.values(getCurrentOrgGroups())
})

const getCurrentVisibleOrgGroups = () => {
    const groups = getCurrentOrgGroups()
    const currentInstructor = instructorModule.getters.getCurrentOrgInstructor()

    if (
        groups
        && !(isPromise(groups))
        && currentInstructor
    ) {
        return currentInstructor.visibleOrgGroups
            .map(visibleGroup => groups[visibleGroup.objectId])
            .filter((group): group is NonNullable<typeof group> => !!group)
    } else {
        return []
    }
}

const getCurrentGroup = () => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const currentOrgGroups = currentOrganization
        && groupModule.state.currentOrgGroups[currentOrganization.objectId]?.value

    if (
        currentOrgGroups
        && !(isPromise(currentOrgGroups))
        && groupModule.state.currentGroupId
    ) {
        const currentGroup = currentOrgGroups[groupModule.state.currentGroupId]
        return currentGroup || null
    } else {
        return null
    }
}

const searchCurrentOrgGroups = (params: {
    type?: Teach.Class.OrgGroupJSON['type']
    examId?: string
    instructorId?: string | null
    studentId?: string
    examGuid?: string
    examMetadataId?: string
}) => {
    const currentOrgExams = examModule.getters.getCurrentOrgExams()
    const examMetadata = examModule.getters.getExamMetadata()

    return currentOrgGroupsList.value.filter(orgGroup => {
        const orgExamForGroup = currentOrgExams[orgGroup.orgExam.objectId]
        const examMetadataForGroup = orgExamForGroup && examMetadata[orgExamForGroup?.examMetadata.objectId]

        if (params.instructorId === null && orgGroup.owner) {
            return false
        }

        return (!params.examId || orgGroup.orgExam.objectId === params.examId)
            && (!params.type || orgGroup.type === params.type)
            && (!params.instructorId || (orgGroup.owner && orgGroup.owner.objectId === params.instructorId))
            && (!params.studentId || orgGroup.orgStudents?.find(student => student.objectId === params.studentId))
            && (!params.examGuid || examMetadataForGroup?.examGuid === params.examGuid)
            && (!params.examMetadataId || examMetadataForGroup?.objectId === params.examMetadataId)
    })
}

// TODO - consider refactor options
const groupsStudentIds = computed(() => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const groups = currentOrganization
        && groupModule.state.currentOrgGroups[currentOrganization.objectId]?.value
    const instructor = instructorModule.getters.getCurrentOrgInstructor()
    const licenses: Study.Class.LicenseJSON[] = licenseModule.getters.getCurrentOrgLicenses()
    const guidOrgExamLib = examModule.getters.getGuidOrgExamLib()

    const activeAndPendingLicensesLib = licenses
        .reduce((acc, l) => {
            const orgExams = l.examGuid && guidOrgExamLib[l.examGuid]
            const orgStudentId = l.orgStudent?.objectId

            if (orgExams) {
                orgExams?.forEach(orgExam => {
                    const orgExamId = orgExam && orgExam.objectId

                    if (!orgExamId || l.status === 'available' || l.status === 'archived') {
                        return
                    }

                    if (!(orgExamId in acc)) {
                        acc[orgExamId] = []
                    }

                    if (orgStudentId) {
                        acc[orgExamId]?.push(orgStudentId)
                    }
                })
            }
            return acc
        }, {} as { [key: string]: string[] })

    if (
        groups
        && !(isPromise(groups))
        && instructor
    ) {
        return Object.values(groups).reduce((acc, g) => {
            const examLicenses = g.orgExam && activeAndPendingLicensesLib[g.orgExam.objectId]
            const activeAndPendingStudents = examLicenses && g.orgStudents.reduce((acc2, s) => {
                if (examLicenses.includes(s.objectId)) {
                    acc2.push(s.objectId)
                }

                return acc2
            }, [] as string[])

            acc[g.objectId] = activeAndPendingStudents || []
            return acc
        }, {} as { [key: string]: string[] })
    }
    return {}
})

// Returns a list of studentIds that INCLUDES pending
const getGroupsStudentIds = () => {
    return groupsStudentIds.value
}

const getGroupName = (groupId: string, type: 'short' | 'long' = 'short') => {
    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    const currentOrgGroups = currentOrganization
        && groupModule.state.currentOrgGroups[currentOrganization.objectId]?.value
    const currentOrgInstructor = instructorModule.getters.getCurrentOrgInstructor()
    const currentOrgInstructors = instructorModule.getters.getCurrentOrgInstructors()
    const currentOrgInstructorUsers = instructorModule.getters.getCurrentOrgInstructorUsers()
    const currentOrgExams = examModule.getters.getCurrentOrgExams()
    const group = currentOrgGroups
        && !(isPromise(currentOrgGroups))
        && currentOrgGroups[groupId]
    const exam = group && group.orgExam && currentOrgExams[group.orgExam.objectId]

    if (
        exam
        && group
        && currentOrgInstructor
        && currentOrgInstructorUsers
        && !(isPromise(currentOrgInstructorUsers))
        && currentOrgInstructors
        && !(isPromise(currentOrgInstructors))
    ) {
        if (group.type !== 'system') {
            return group.name
        }

        const ownerOrgInstructor = group.owner && currentOrgInstructors[group.owner.objectId]
        const ownerUser = ownerOrgInstructor && currentOrgInstructorUsers[ownerOrgInstructor.user.objectId]

        if (group.owner && ownerUser) {
            const name = ownerUser.firstName || ownerUser.lastName
                ? [ ownerUser.firstName, ownerUser.lastName?.slice(0, 1) ].join(' ')
                : ownerUser.username
            return type === 'short'
                ? currentOrgInstructor.isAdmin
                    ? `${exam.displayName} - ${name}`
                    : exam.displayName
                : `${exam.displayName} <span>(${name})</span>`
        } else if (!group.owner && currentOrgInstructor.isAdmin && type === 'long') {
            return `${exam.displayName} <span>(all students)</span>`
        } else {
            return exam.displayName
        }
    }

    return ''
}

const basicStudents = computed(() => {
    const studentLicenseLib = licenseModule.getters.getStudentLicenseLib()
    const currentOrgExams = examModule.getters.getCurrentOrgExams()
    const currentOrgStudents = studentModule.getters.getCurrentOrgStudents()
    const examMetadata = examModule.getters.getExamMetadata()
    const systemExamGroups = currentOrgGroupsList.value.filter(g => g.type === 'system' && !g.owner)

    const basicStudentsList = systemExamGroups.reduce((acc, og) => {
        const orgExam = currentOrgExams[og.orgExam.objectId]
        const em = orgExam && examMetadata[orgExam?.examMetadata.objectId]
        const currentExam = examModule.getters.getCurrentGroupExam()
        const isInCurrentExam = currentExam?.objectId === og.orgExam.objectId

        if (!orgExam || !em || !isInCurrentExam) {
            return acc
        }

        // iterate over orgStudents adding them if they don't have a license and aren't archived
        og.orgStudents.forEach(s => {
            const orgStudent = currentOrgStudents[s.objectId]
            const studentLicenses = studentLicenseLib[s.objectId]
            const examLicense = studentLicenses?.find(l => l.examGuid === em.examGuid)

            /**
             * Probably lingering from previous bugs, but there are OrgStudents with no user pointer, no
             * display name, and no license(s). We shouldn't use them...
             **/
            if (!orgStudent || orgStudent.isArchived || examLicense || !orgStudent.user) {
                return
            }
            acc.push(orgStudent.objectId)
        })

        return acc
    }, [] as string[])

    // Ensure uniqueness
    return [ ...new Set(basicStudentsList) ]
})

const getBasicStudents = () => {
    return basicStudents.value
}

const groupCompositeKeysByGroupId = computed(() => {
    const orgExamIdGuidLib = examModule.getters.getOrgExamIdGuidLib()

    return currentOrgGroupsList.value.reduce((acc, orgGroup) => {
        const examGuid = orgExamIdGuidLib[orgGroup?.orgExam.objectId]
        if (!orgGroup || !examGuid) {
            return acc
        }
        const groupCompositeKey = `${examGuid}/${orgGroup.owner?.objectId || 'system'}/${orgGroup.name}`
        acc[orgGroup.objectId] = groupCompositeKey
        return acc
    }, {} as { [orgGroupId: string]: string })
})

const getGroupCompositeKeysByGroupId = () => {
    return groupCompositeKeysByGroupId.value
}

const getGroupCompositeKeyForGroupId = (orgGroupId: string) => {
    return groupCompositeKeysByGroupId.value[orgGroupId]
}

const groupsByGroupCompositeKey = computed(() => {
    return currentOrgGroupsList.value.reduce((acc, orgGroup) => {
        const groupCompositeKey = getGroupCompositeKeyForGroupId(orgGroup.objectId)
        if (!groupCompositeKey) {
            return acc
        }
        if (acc[groupCompositeKey]) {
            acc[groupCompositeKey].push(orgGroup)
        } else {
            acc[groupCompositeKey] = [ orgGroup ]
        }

        return acc
    }, {} as { [ groupCompositeKey: string ]: Teach.Class.OrgGroupJSON[] })
})

const getGroupsByGroupCompositeKey = () => {
    return groupsByGroupCompositeKey.value
}

const compositeGroups = computed(() => {
    const examMetadata = examModule.getters.getExamMetadata()
    const currentOrgExams = examModule.getters.getCurrentOrgExams()
    const currentOrgInstructors = instructorModule.getters.getCurrentOrgInstructors()
    const currentOrgInstructorUsers = instructorModule.getters.getCurrentOrgInstructorUsers()
    const groupsByCompositeKey = getGroupsByGroupCompositeKey()

    return Object.entries(groupsByCompositeKey)
        .filter(([ , groups ]) => {
            const sampleGroup = groups[0]
            const orgExam = sampleGroup?.orgExam && currentOrgExams[sampleGroup.orgExam.objectId]
            const examMetadataForOrgExam = orgExam && examMetadata[orgExam?.examMetadata.objectId]
            return !!orgExam && !!examMetadataForOrgExam
        })
        .map(([ groupCompositeKey, groups ]) => {
            const sampleGroup = groups[0]
            const orgExam = sampleGroup?.orgExam && currentOrgExams[sampleGroup.orgExam.objectId]
            const examName = orgExam?.displayName || ''
            const examGuid = orgExam && examModule.getters.getExamGuidForOrgExamId(orgExam.objectId) || ''

            const orgInstructor = sampleGroup?.owner
                && currentOrgInstructors?.[sampleGroup.owner.objectId]
            const instructorUser = orgInstructor
                && currentOrgInstructorUsers?.[orgInstructor.user.objectId]
            const creator = sampleGroup?.type === 'system'
                ? 'System'
                : instructorUser?.firstName || ''
            const shortName = sampleGroup && groupModule.getters.getGroupName(sampleGroup.objectId, 'short') || ''
            const longName = sampleGroup && groupModule.getters.getGroupName(sampleGroup.objectId, 'long') || ''

            if (!orgExam || !sampleGroup) {
                return
            }

            const compositeGroup: TCompositeGroup = {
                id: groupCompositeKey,
                groups,
                shortName,
                longName,
                creator,
                examGuid,
                orgStudents: sampleGroup.orgStudents,
                orgExam,
                examName,
                type: sampleGroup.type,
                owner: sampleGroup.owner,
                isPubliclyEditable: sampleGroup.isPubliclyEditable,
                organization: sampleGroup.organization,
            }

            return compositeGroup
        })
})

const getCompositeGroups = () => {
    return compositeGroups.value
}

const getCurrentGroupForGroupCompositeKey = (groupCompositeKey: string) => {
    const groupsInCompositeGroup = groupModule.getters.getGroupsByGroupCompositeKey()[groupCompositeKey]
    const sampleGroup = groupsInCompositeGroup?.[0]
    if (!sampleGroup) {
        return null
    }

    const currentOrgExamForExamGuid = examModule.getters.getCurrentOrgExamForOrgGroupId(sampleGroup.objectId)
    const currentOrgExamId = currentOrgExamForExamGuid && currentOrgExamForExamGuid.objectId
    const currentGroup = groupsInCompositeGroup.find(g => g.orgExam.objectId === currentOrgExamId)
    return currentGroup
}

const groupsByStudentId = computed(() => {
    const allGroups = currentOrgGroupsList.value
    return allGroups.reduce((acc, orgGroup) => {
        orgGroup.orgStudents.forEach(orgStudent => {
            const studentId = orgStudent.objectId
            if (!(studentId in acc)) {
                acc[studentId] = []
            }
            acc[studentId]?.push(orgGroup)
        })
        return acc
    }, {} as { [studentId: string]: Teach.Class.OrgGroupJSON[] })
})

const getGroupsByStudentId = () => {
    return groupsByStudentId.value
}

const getGroupsForStudentId = (studentId: string) => {
    const groupsForStudentId = getGroupsByStudentId()[studentId]
    return groupsForStudentId
}

const getIsStudentInGroup = (params: { studentId: string; groupId: string }) => {
    const studentGroups = getGroupsForStudentId(params.studentId)
    const studentGroupIdSet = new Set(studentGroups?.map(studentGroup => studentGroup.objectId))
    const groupCompositeKey = getGroupCompositeKeyForGroupId(params.groupId)
    const groupsInCompositeGroup = groupCompositeKey ? getGroupsByGroupCompositeKey()[groupCompositeKey] : []
    // If the student is in any group from the composite groups, then they should be considered in the group
    const isStudentInGroup = groupsInCompositeGroup?.some(group => studentGroupIdSet.has(group.objectId))
    return isStudentInGroup
}

export default {
    getCurrentOrgGroups,
    getCurrentVisibleOrgGroups,
    searchCurrentOrgGroups,
    getCurrentGroup,
    getGroupsStudentIds,
    getBasicStudents,
    getGroupCompositeKeysByGroupId,
    getGroupCompositeKeyForGroupId,
    getGroupsByGroupCompositeKey,
    getCompositeGroups,
    getGroupName,
    getCurrentGroupForGroupCompositeKey,
    getGroupsByStudentId,
    getGroupsForStudentId,
    getIsStudentInGroup,
}
